// Quality i18n namespace (Module 07 §4, slice 07-A). Wired into apps/web's
// aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically, and
// declared in src/i18n-completeness.ts so the VALUE gate knows which locales are
// real.
//
// `qualityEn` is authoritative; `QualityDict = typeof qualityEn` makes every locale
// parity-checked at COMPILE time (a missing or extra key fails tsc). `ru` is a
// real translation (quality.ru.ts) — the design partner's qc lead configures and
// runs these screens in Russian. fr, zh, zh-Hant and hi live in their own
// quality.<locale>.ts files, loaded only with that locale (D-430), and keep
// the three words below apart in every language.
//
// THE THREE WORDS THAT MUST STAY THREE WORDS (D-123 ruling 9, §2.7's
// amendment). A factory user meets three unrelated things this platform calls a
// "hold": module 04's `work_order_operations.qc_hold` (a boolean on an
// OPERATION), module 07's `qc_holds` (a quarantine RECORD on stock), and module
// 06's `reservations` (which have nothing to do with quality). The ruling: this
// module's own record is spoken as QUARANTINE, module 04's flag as "quality hold
// ON THE OPERATION", module 06's as "reservation" — in en AND ru together,
// because a term settled in English and left to a later translator in Russian is
// not settled.
//
// SINCE 07-B-3 THE WORD IS ON CONTROLS, NOT JUST SENTENCES: the Quarantine
// Board (§4.6, `board.*`), the §4.4 failure-action sheet (`run.sheet.*`) and
// the §4.3 money chips all speak it. The three-words rule is load-bearing on
// this dictionary: `qc_holds` → "quarantine", module 04's flag → "quality
// hold on the operation" (`queue.blocks.in_process`, `editor.blockingHint`),
// module 06's reservations → "reservation" — never one word for two of them.
//
// ENTITY NOUNS ARE NOT IN HERE. "Bobbin", "Drum", "Lot" are TERMINOLOGY keys
// resolved through `useTerm()` (module 02 §2.5). Control-point names, check-item
// names and failure-cause names are not here either, and could not be: they are
// TENANT DATA (`name`, `name_i18n` on the row), which is exactly why the editor
// shows one instruction field per locale.

import type { Translated } from "../../../i18n-shape.js";
import { qualityRuDict } from "./quality.ru.js";

export const qualityEn = {
  nav: {
    aria: "Quality sections",
    inspections: "Inspections",
    quarantine: "Quarantine",
    controlPoints: "Control points",
    failureCauses: "Failure causes",
    coa: "Certificates",
    analytics: "Analytics",
  },

  // §4.8 — quality analytics (07-E).
  analytics: {
    title: "Quality analytics",
    refresh: "Refresh projection",
    asOf: "As of {{at}} (projection refresh)",
    filterFrom: "From",
    filterTo: "To",
    filterFacility: "Facility",
    allFacilities: "All facilities",
    filterDimension: "Group by",
    filterBy: "Rank by",
    filterDate: "Date",
    // D-123 r8 (review F13): this module's yield is ALWAYS the labelled
    // "first-pass yield (inspections)" — module 04's quantity yield is the
    // other number, and a bare "FPY" lets the two be mistaken.
    fpyLabel: "First-pass yield (inspections)",
    trendTitle: "First-pass yield (inspections) — trend",
    trendLatest: "Latest: {{value}} on {{day}}",
    leagueTitle: "First-pass yield (inspections) by dimension",
    filterGrain: "Grain",
    grainDaily: "Daily",
    grainWeekly: "Weekly",
    unstampedKey: "(no dimension stamped)",
    unattributed: "(no shift stamped)",
    paretoTitle: "Failure Pareto",
    scoreboardTitle: "Shift scoreboard",
    supplierTitle: "Supplier receiving failures",
    dimProduct: "Product",
    dimWorkCenter: "Work centre",
    dimShift: "Produced shift",
    byCount: "By count",
    byValue: "By value at risk",
    colKey: "Dimension",
    colTotal: "Inspections",
    colPassedFirstTime: "Passed first time",
    colCause: "Cause",
    colCount: "Count",
    colValue: "Value at risk",
    colShift: "Shift",
    colInspections: "Inspections",
    colDefectsProduced: "Defects produced",
    colDefectsCaught: "Defects caught",
    colHandoffs: "Hand-off incidents",
    colSupplier: "Supplier",
    colReceipts: "Receipts",
    colFailures: "Failures",
    colFailureRate: "Failure rate",
    colTopCause: "Top cause",
    error: "Could not load the analytics.",
    emptyTitle: "No inspections in this window",
    emptyBody: "Widen the date range, or check that inspections completed in it.",
    scoreEmptyTitle: "No shifts on this date",
    scoreEmptyBody: "Pick a date the facility ran shifts on.",
    supplierEmptyTitle: "No receiving inspections in this window",
    supplierEmptyBody: "Receiving samples appear here once gated materials arrive.",
  },

  // §4.7 — the CoA manager (07-E). "Certificate" is the user's word — CoA is
  // the industry abbreviation and rides the title; every sentence below says
  // certificate.
  coa: {
    title: "Certificates (CoA)",
    tabDocuments: "Documents",
    tabTemplates: "Templates",
    filterStatus: "Status",
    allStatuses: "All statuses",
    colNo: "Certificate",
    colRevision: "Rev",
    colKind: "Kind",
    colCustomer: "Customer",
    colLanguage: "Language",
    colStatus: "Status",
    colIssuedAt: "Issued",
    colActions: "Actions",
    kind: { analysis: "Analysis", conformance: "Conformance" },
    status: { draft: "Draft", issued: "Issued", revoked: "Revoked" },
    preview: "Preview",
    pdf: "PDF",
    copyLink: "Copy public link",
    linkCopied: "Public link copied.",
    issue: "Issue",
    issued: "Certificate {{coaNo}} issued — the public link is live.",
    issueError: "Could not issue the certificate.",
    revoke: "Revoke",
    revokeTitle: "Revoke {{coaNo}}",
    revokeWarning:
      "Revoking kills the customer link immediately — anyone holding it gets 404 from this moment. The frozen PDF stays in your records.",
    reissueNext: "Draft the next revision from the same certificate number",
    revoked: "Certificate {{coaNo}} revoked.",
    revokeError: "Could not revoke the certificate.",
    fieldReason: "Reason",
    draftNew: "Draft certificate",
    draftSubmit: "Create draft",
    drafted: "Draft {{coaNo}} created.",
    draftError: "Could not draft the certificate.",
    fieldTemplate: "Template",
    fieldLot: "Lot",
    fieldLotHint: "The lot id to certify — every current certificate is drafted from a lot.",
    fieldCustomer: "Customer name",
    fieldCustomerHint: "Printed on the certificate exactly as typed.",
    fieldLanguage: "Language",
    fieldKind: "Kind",
    gapsTitle: "{{coaNo}} cannot be issued yet — these measurements are missing or failed:",
    gapMissing: "Run {{point}} on {{unit}}",
    gapNotPassed: "{{point}} did not pass on {{unit}} — resolve the failure first",
    error: "Could not load the certificates.",
    emptyTitle: "No certificates yet",
    emptyBody: "Draft one from a lot whose final inspections have passed.",
    tplColName: "Template",
    tplColBinding: "Product binding",
    tplColActive: "State",
    tplActive: "Active",
    tplInactive: "Inactive",
    tplAllProducts: "All products",
    tplNew: "New template",
    tplEdit: "Edit",
    tplSave: "Save template",
    tplSaved: "Template saved.",
    tplError: "Could not save the template.",
    tplEmptyTitle: "No templates yet",
    tplEmptyBody: "Create one — a certificate cannot be drafted without a template.",
    tplFieldName: "Name",
    tplFieldProduct: "Product binding",
    tplFieldProductHint: "Empty = the default template for every product.",
    tplFieldHeaderEn: "Header (English)",
    tplFieldHeaderRu: "Header (Russian)",
    tplFieldSignatory: "Signatory name",
  },

  common: {
    retry: "Retry",
    requestId: "Request ID",
    saving: "Saving…",
    cancel: "Cancel",
    loading: "Loading…",
    // The analytics tables' client pager (rev-7, D-426).
    range: "{{from}}–{{to}} of {{total}}",
    prevPage: "Previous page",
    nextPage: "Next page",
  },

  // A REFUSAL, said as a refusal. Not an error card: an error card offers Retry,
  // and a Retry that can never succeed teaches the reader that the platform is
  // flaky when it is doing exactly what it was configured to do.
  access: {
    title: "Not your screen",
    area: "Your role has no quality screens. Ask a group administrator if you need them.",
    queue:
      "Your role does not read the inspection queue. A group administrator can grant it if this is part of your job.",
    coa: "Your role does not read certificates. A group administrator can grant it if this is part of your job.",
    analytics:
      "Your role does not read quality analytics. A group administrator can grant it if this is part of your job.",
    board:
      "Your role does not read the quarantine board. A group administrator can grant it if this is part of your job.",
    library:
      "Your role does not read the control-point library. A group administrator can grant it if this is part of your job.",
    causes:
      "Your role does not read the quality catalogue. A group administrator can grant it if this is part of your job.",
    editor:
      "Only quality staff and administrators define control points. You can read them from the library.",
  },

  // A PAUSED READ, not a slow one. The request is held until the network is
  // back; a loading skeleton here would shimmer for as long as the cut lasts.
  offline: {
    title: "No network",
    body: "This device is offline. The screen fills itself in as soon as the connection returns — nothing was lost.",
  },

  // §2.1's three stages (closed set — the wire stays canonical English codes;
  // these are the labels the chips and filters render).
  stage: {
    receiving: "Receiving",
    in_process: "In process",
    final: "Final",
  },

  // §3.4's state machine.
  status: {
    open: "Not started",
    in_progress: "In progress",
    passed: "Passed",
    failed: "Failed",
    cancelled: "Cancelled",
  },

  verdict: {
    pass: "Pass",
    fail: "Fail",
    na: "Not applicable",
  },

  // §2.3's four check types.
  checkType: {
    pass_fail: "Pass / fail",
    measurement: "Measurement",
    photo: "Photo",
    checklist: "Checklist",
  },

  // §2.1's sampling vocabulary, as the editor's picker shows it.
  sampleMode: {
    every_unit: "Every unit",
    fixed_count: "A fixed number",
    percentage: "A percentage",
    first_n: "The first N",
    aql: "AQL sampling plan",
  },

  // 07-D — the §2.5 wire echo (D-192), RENDERED and never recomputed (D-195:
  // the AQL tables live in domain-kernel, which this app does not depend on).
  // The aql sentence is the queue's "sample 5 of 50, accept 0"; `lot_whole`
  // is D-194's bulk arm, which §2.1's configured vocabulary never carries.
  samplingEcho: {
    aql: "sample {{n}} of {{lotSize}}, accept {{accept}}",
    lotWhole: "Whole lot (bulk receipt)",
  },

  // The same vocabulary as a LIBRARY CELL — with the parameter, because
  // "a percentage" without the percentage is not a rule anybody can check.
  sample: {
    every_unit: "Every unit",
    fixed_count: "{{n}} per lot",
    first_n: "First {{n}}",
    percentage: "{{pct}}% sampled",
    aql: "AQL {{value}}, level {{level}}",
  },

  // §2.4's cause categories (closed set).
  causeCategory: {
    material: "Material",
    machine: "Machine",
    method: "Method",
    operator: "Operator",
    environment: "Environment",
    unknown: "Not known",
  },

  locale: {
    en: "English",
    ru: "Russian",
    fr: "French",
    zh: "Chinese (Simplified)",
    "zh-Hant": "Chinese (Traditional)",
    hi: "Hindi",
  },

  // §2.7's disposition vocabulary — what happens to HELD STOCK. Deliberately
  // NOT module 04's defect-handoff wording: three values are spelled the same
  // on the wire and none means the same thing (D-123 ruling 1).
  disposition: {
    release: "Release",
    rework: "Rework",
    scrap: "Scrap",
    downgrade: "Downgrade",
    return_to_supplier: "Return to supplier",
    // Never offered in the dialog. The guided cut writes it when it disposes
    // the quarantine it cut, and the Disposed tab shows it (§3.5d).
    guided_cut: "Guided cut",
  },

  // S4.6 — the Quarantine Board (07-B-3). Every string says QUARANTINE
  // (D-123 ruling 9); module 06's reservations, where they ever render near
  // this table, are "reservations" and module 04's flag is "the quality hold
  // on the operation".
  board: {
    title: "Quarantine",
    tabsAria: "Quarantine board",
    tab: {
      active: "Active",
      released: "Released",
      disposed: "Disposed",
    },
    tableCaption: "Quarantined stock, newest first",
    colSelect: "Select",
    selectAll: "Select every quarantine on this page",
    selectOne: "Select quarantine {{holdNo}}",
    colNo: "No.",
    colSubject: "Subject",
    colReason: "Why",
    colInspection: "Inspection",
    inspectionLink: "Open the inspection",
    manualHold: "Placed by hand",
    colAge: "Held for",
    ageDays: "{{days}} d",
    // A9 — an UNPARSABLE placed_at renders the dash, never "0 d": zero is a
    // value (placed today) and a wire instant this client cannot read is not
    // that value (D-121's rendered-numbers rule, applied defensively).
    noAge: "—",
    colAtRisk: "Value at risk",
    colPlacedBy: "Placed by",
    colProposal: "Agent proposal",
    proposalChip: "Proposed: {{kind}}",
    noProposal: "—",
    colDecision: "Decision",
    noSubject: "—",
    filterProduct: "Product",
    anyProduct: "Any product",
    // A4 / D-118 — the picker's TRANSIENT failure is a dead control with its
    // reason, never absence (absence is the permission answer).
    productFilterUnavailable:
      "The product list did not load — the filter is unavailable right now.",
    filterAge: "Held at least (days)",
    loadMore: "Show more",
    error: "Could not load the quarantine board",
    empty: {
      activeTitle: "Nothing quarantined",
      activeBody:
        "Stock lands here when a failed check quarantines it automatically or somebody places a quarantine by hand.",
      releasedTitle: "Nothing released yet",
      releasedBody: "A quarantine released with its justification collects here.",
      disposedTitle: "Nothing disposed yet",
      disposedBody:
        "Quarantines resolved as rework, scrap, downgrade or a supplier return collect here.",
    },
    dispositionOpen: "Decide {{count}} selected…",
    dialogTitle: "Decide {{count}} quarantined item(s)",
    dialogKind: "Decision",
    dialogKindPick: "Choose what happens to the stock…",
    dialogKindRequired: "Choose a decision first.",
    dialogNotes: "Note",
    dialogJustificationRequired: "A release carries a mandatory justification note.",
    dialogNoteRequired: "A downgrade carries a mandatory note.",
    dialogProposed: "An agent proposed {{kind}} for this quarantine. You are the decision.",
    // 07-D — §3.5c's cohort decision on a receiving-sample quarantine. The
    // three labels say what each DOES, and the whole_lot note NAMES THE LOT
    // (D-193): a lot is a superset of a receipt — one that took several
    // deliveries condemns all of them, and the operator reads that before
    // pressing, not after.
    scopeLabel: "Cohort decision",
    scopePick: "Choose what happens to the rest of the delivery…",
    scopeRequired: "A receiving-sample quarantine needs the cohort decision too.",
    scope: {
      sampled_only: "This sample only — release the lot",
      whole_lot: "Condemn the whole lot",
      inspect_all: "Inspect every remaining unit",
    },
    scopeLotNote:
      "This applies the decision to the WHOLE LOT — a lot that took several deliveries condemns all of them. Lot:",
    // A1 — the rework TARGET, offered for a single selected quarantine that is
    // manual or whose inspection names no operation (§3.5a's `work_order_
    // operation_id` on the §5 disposition row). The server's rework-target
    // refusal renders as this field's helper, so the dead-end is gone.
    targetLabel: "Operation to re-open — optional",
    targetHint: "Paste the work-order operation the rework should re-open",
    targetInvalid: "Paste a full operation ID, or leave it empty.",
    // D-299 ruling G5, the §4.6 dialog's half: Rework is dead when the single
    // selected quarantine's inspection targets a step on a work order that is
    // no longer released or in progress. The hold door never takes "accept
    // deviation", so this names the three it does (spec 07 §3.5a ruling G2).
    reworkOrderNotLive:
      "Rework is not available: the work order is {{status}}. Release this quarantine with a justification, scrap it or downgrade it.",
    // D-299 owner ruling Q4: the dialog is judged through the same rework-door
    // preview as the §4.4 sheet. A step TYPED into the target field on a
    // finished order blocks Confirm (the field is still a way through); the
    // inspection's OWN step that is not completed makes rework unavailable at
    // this shape, which offers no field to name another step.
    reworkTargetNotLive:
      "The work order is {{status}}. Name a step on a live order, or release this quarantine with a justification, scrap it or downgrade it.",
    reworkStepNotCompleted:
      "Rework is not available: step {{seq}} is {{status}}, so there is nothing to reopen. Release this quarantine with a justification, scrap it or downgrade it.",
    dialogConfirm: "Confirm for {{count}} item(s)",
    outcomeDone: {
      active: "Still active",
      released: "Released",
      disposed: "Disposed",
    },
    // §4.6's concurrent-disposition state, rendered from the §9.2 409 body:
    // exactly one decision wins, and the loser reads WHO and WHEN — at the
    // grain the instant deserves (R7): "0 h ago" told a qc lead a decision
    // taken thirty seconds ago was stale news.
    concurrent: "Already disposed — {{kind}} by {{user}}, {{hours}} h ago.",
    concurrentMinutes: "Already disposed — {{kind}} by {{user}}, {{minutes}} min ago.",
    concurrentJustNow: "Already disposed — {{kind}} by {{user}}, just now.",
    // R4 — a TRANSPORT failure has no server sentence to relay, and "Failed to
    // fetch" is not a sentence a qc lead acts on. Honest about the ambiguity:
    // a lost answer is not a refusal.
    transportFailed:
      "No answer came back, so this decision may or may not have been recorded. Refresh the board to check.",
    refreshPrompt: "Somebody else decided first. Refresh the board to see the winning decision.",
    refreshBoard: "Refresh the board",
  },

  // S4.1 — the control point library.
  library: {
    title: "Control points",
    newPoint: "New control point",
    tableCaption: "Control points",
    colCode: "Code",
    colName: "Name",
    colStage: "Stage",
    colBindings: "Applies to",
    colSample: "Sampling",
    colFlags: "Flags",
    colVersion: "Version",
    colActions: "Actions",
    versionCell: "v{{version}}",
    superseded: "superseded",
    blocking: "Blocking",
    fromPack: "Industry pack",
    custom: "Yours",
    inactive: "Turned off",
    edit: "Edit",
    duplicate: "Duplicate",
    deactivate: "Turn off",
    deactivateFailed: "Could not turn that control point off.",
    filterStage: "Stage",
    allStages: "All stages",
    filterSource: "Comes from",
    allSources: "Any source",
    filterActive: "State",
    anyActive: "On or off",
    onlyActive: "On",
    onlyInactive: "Off",
    filterVersions: "Versions",
    onlyCurrent: "Current only",
    allVersions: "Every version",
    filterSearch: "Search",
    searchHint: "Code or name…",
    boundOnRouting: "A routing step (set in the catalogue)",
    boundNothing: "Nothing yet",
    boundMaterials: "{{count}} materials",
    boundProducts: "{{count}} products",
    boundSuppliers: "{{count}} suppliers",
    error: "Could not load the control points",
    emptyTitle: "No control points yet",
    emptyBody:
      "A control point says where quality is checked and what is measured. Install the cable pack's six, or write your own.",
    seedPack: "Install the cable pack's checks",
    // §9.15 — seeding is idempotent per code, so the answer has TWO halves and
    // one total would hide the difference between "installed six" and "found
    // six already yours and touched none".
    seedReport:
      "Installed {{created}} control points, left {{skipped}} of yours untouched, added {{causes}} failure causes.",
    seedFailed: "Could not install the pack's checks.",
  },

  // S4.1b — THE FAILURE-CAUSE CATALOG (fix #117).
  //
  // Every sentence here says CONSEQUENCE before mechanism, because the reader
  // arrives from a 422 that stopped them scrapping a drum: "unmapped" is a word
  // about a database column, and "a scrap on this cause is refused" is what
  // they are actually looking at. The waste vocabulary stays module 05's —
  // "waste cause", the word its own screens use — so the two catalogs read as
  // two catalogs and not as one thing with two names.
  causes: {
    title: "Failure causes",
    tableCaption: "Failure causes",
    explainer:
      "A failure cause says why a check failed. Link it to a waste cause so the material can be scrapped against it.",
    // ONE form with a count, not i18next plurals: `QualityDict` makes ru a
    // key-for-key mirror of en, and Russian needs one/few/many where English
    // needs one/other — so a `_one`/`_other` pair here would make the parity
    // type unsatisfiable in the very locale that is translated. Every other
    // count string in this dictionary is written the same way.
    unmappedCount:
      "Causes with no waste cause: {{count}}. Scrapping against them is refused until they are mapped.",
    colCode: "Code",
    colName: "Name",
    colCategory: "Kind",
    colMapping: "Waste cause",
    colActions: "Actions",
    unmapped: "Not mapped",
    map: "Map",
    remap: "Change",
    filterCategory: "Kind",
    allCategories: "Any kind",
    filterMapping: "Waste cause",
    allMappings: "Mapped or not",
    onlyUnmapped: "Not mapped only",
    error: "Could not load the failure causes",
    emptyTitle: "No failure causes yet",
    emptyBody: "Failure causes come with the industry pack, and quality staff can add their own.",
    emptyFiltered: "Nothing matches these filters.",
    dialogTitle: "Map {{code}} to a waste cause",
    dialogBody:
      "Pick the waste cause this failure should be recorded under. It applies to scraps made from now on — inspections already decided keep what they recorded.",
    dialogField: "Waste cause",
    dialogPickPlaceholder: "Choose a waste cause…",
    dialogSave: "Save the mapping",
    dialogPickRequired: "Choose a waste cause first.",
    dialogOffline:
      "You are offline. A change to this list needs the connection, so it waits until you are back online.",
    dialogLoadingOptions: "Loading the waste causes…",
    dialogOptionsFailed:
      "The waste causes did not load, so there is nothing to choose from yet. Close this and try again.",
    dialogNoOptions:
      "There are no active waste causes yet. A group administrator adds them in Waste → Settings first.",
    dialogFailed: "Could not save the mapping.",
  },

  // S4.2 — the editor.
  editor: {
    itemUnnamed: "Unnamed check",
    itemFold: "Close",
    itemOpen: "Edit",
    otherLanguages: "Other languages",
    newTitle: "New control point",
    editTitle: "Edit control point",
    // The CONSEQUENCE, not the action. A Save that mints an immutable revision
    // and supersedes the one every open inspection pinned is not something to
    // leave anybody to infer.
    versionBanner:
      "Saving creates version {{next}}. Inspections already open keep running against the version they started on.",
    duplicateBanner: "Copied from another control point. Give it a code of its own before saving.",
    loadError: "Could not load that control point",
    code: "Code",
    name: "Name",
    stage: "Stage",
    stageLocked: "The stage is fixed once a control point exists — it decides what can be bound.",
    sampling: "Sampling",
    sampleMode: "How much is checked",
    sampleSize: "How many",
    samplePct: "Percent",
    aqlLevel: "Inspection level",
    aqlValue: "Acceptable quality limit",
    behaviour: "What a failure does",
    blocking: "Blocking",
    // D-123 ruling 5: what "blocking" fences differs by stage, and the
    // in-process answer is NOT what most people assume. Stated at the toggle.
    blockingHint: {
      receiving: "Received stock is held until this check passes — it cannot be issued or shipped.",
      in_process:
        "Puts a quality hold on the operation. It stops rework on a reopened step, not the next operation. Quarantine holds the material.",
      final: "The unit cannot be allocated to a shipment until this check passes.",
    },
    autoHold: "Quarantine the stock on failure",
    autoHoldHint:
      "A failed check quarantines the unit or lot automatically and it appears on the Quarantine Board, where a quality lead decides what happens to it.",
    guidedCut: "Offer a guided cut on failure",
    trimAllowance: "Trim each side of the fault (m)",
    minSellable: "Shortest piece worth keeping (m)",
    bindings: "Applies to",
    bindingsOnRouting:
      "In-process points are attached to a routing step in the catalogue's routing editor, not here.",
    bindingMaterial: "Material",
    bindingProduct: "Product",
    bindingHint: "Pick from the catalogue",
    bindingUnresolved: "Already bound — not in this list",
    addBinding: "Add one",
    bindingsShowAll: "Show all {{count}}",
    removeBinding: "Remove",
    checkItems: "Checks",
    itemSeq: "Check {{seq}}",
    itemName: "What is checked",
    itemType: "Kind",
    itemUom: "Unit",
    itemMin: "Minimum",
    itemTarget: "Target",
    itemMax: "Maximum",
    itemChecklist: "Rows",
    itemChecklistHint: "insulation_color, marking",
    itemRequired: "Must be answered",
    itemPhoto: "Needs a photo",
    itemMinPhotos: "How many photos",
    itemCoa: "Print on certificates",
    addItem: "Add a check",
    removeItem: "Remove",
    moveUp: "Up",
    moveDown: "Down",
    instructions: "Operator instructions",
    instructionsHint:
      "Shown on the floor above the checks. Leave a language empty if you have no translation.",
    saveNew: "Create control point",
    saveVersion: "Save as a new version",
    saveFailed: "The save was refused.",
    invalid: "{{count}} things need fixing before this can be saved.",
    errCode: "A code is capitals, digits, underscores and dashes — for example CP-CONT-FINAL.",
    errName: "Give it a name.",
    errSampleSize: "How many units? A whole number.",
    errSamplePct: "A percentage above 0 and up to 100.",
    errInProcessBindings: "In-process points are bound on a routing step, not here.",
    errFinalBindings: "A final check needs at least one product.",
    errReceivingBindings: "A receiving check needs at least one material.",
    errBindingProduct: "Paste a product ID.",
    errBindingMaterial: "Paste a material ID.",
    errNoItems: "A control point needs at least one check.",
    errItemName: "Say what is checked.",
    errItemDuplicate:
      "Two checks cannot have the same name — the floor follows this list in order.",
    errItemUom: "A measurement needs a unit.",
    errItemBounds: "A measurement needs a minimum, a maximum, or both.",
    errItemMinMax: "The minimum cannot be above the maximum.",
    errItemDecimal: "Up to six decimal places, digits only.",
    errItemChecklist: "List at least one row.",
    errItemPhotos: "At least one photo.",
  },

  // S4.4's office host — the detail-only lines (07-D).
  detail: {
    samplingLabel: "Sampling plan:",
  },

  // S4.3 — the inspection queue.
  queue: {
    title: "Inspections",
    tabsAria: "Inspection queue",
    tab: {
      open: "Not started",
      in_progress: "In progress",
      failed: "Failed — undecided",
      done: "Done",
    },
    // The `done` tab asks for `passed`, because the API takes one status at a
    // time. Saying so beats silently under-reporting.
    doneNote:
      "Passed inspections. Cancelled ones are not listed here — open them from the inspection they belong to.",
    filterStage: "Stage",
    // 07-D — the cohort badge: rows that arrived with one receipt sample read
    // as ONE decision (D-192's grouping half). The count is the sample size.
    // Never "batch" or "lot": a batch is a production batch, and a lot is a
    // superset of a cohort (D-193).
    cohortBadge: "Sample of {{count}}",
    cohortTitle: "Inspections created by the same receipt sample",
    tableCaption: "Inspections, newest first",
    colNo: "No.",
    colPoint: "Check",
    colSubject: "Subject",
    colStage: "Stage",
    colStatus: "Status",
    colBlocking: "Blocks",
    colAtRisk: "At risk",
    // R5 — the chip walk is BOUNDED (five pages of 200), and when the bound
    // bites the screen says so instead of adding a fourth silent meaning to an
    // absent chip.
    atRiskBounded:
      "Values at risk cover the newest {{count}} quarantine records — older quarantines show nothing here. The quarantine board is the full money view.",
    colAge: "Waiting",
    colCompleted: "Finished",
    ageHours: "{{hours}} h",
    notCompleted: "—",
    notBlocking: "—",
    noSubject: "—",
    unresolved: "No decision yet",
    blocks: {
      receiving: "Blocks the receipt",
      in_process: "Quality hold on the operation",
      final: "Blocks the shipment",
    },
    loadMore: "Show more",
    error: "Could not load the inspection queue",
    empty: {
      openTitle: "Nothing to inspect",
      openBody:
        "Inspections appear here when a receipt lands, an operation finishes, or somebody raises one by hand.",
      in_progressTitle: "Nothing part-finished",
      in_progressBody: "An inspection lands here as soon as its first result is recorded.",
      failedTitle: "No failures waiting on a decision",
      failedBody: "A failed check stays here until somebody decides what happens to the material.",
      doneTitle: "Nothing finished yet",
      doneBody: "Passed inspections collect here.",
    },
  },

  // S4.4 — the execution screen, hosted by BOTH shells.
  run: {
    title: "Inspection",
    loadError: "Could not load that inspection",
    blocking: "Blocking",
    // M07-S12: which version is being executed, on screen, always.
    versionPin:
      "Checked against {{code}} version {{version}}, the version in force when this inspection started.",
    subjectUnit: "Unit",
    subjectLot: "Lot",
    subjectOperation: "Operation",
    subjectWorkOrder: "Work order",
    subjectQty: "Quantity",
    readOnly: "You can read this inspection. Recording results is a quality or floor job.",
    closed: {
      passed: "This inspection passed. Nothing further to record.",
      failed: "This inspection failed.",
      cancelled: "This inspection was cancelled.",
    },
    // §2.5's resolution_kind, rendered — which §3.5 workflow closed the
    // failure (D-134: the WORKFLOW, whichever door reached it).
    resolvedAs: {
      rework: "Resolved — sent to rework.",
      scrap: "Resolved — scrapped and recorded in the waste ledger.",
      hold_disposition: "Resolved — decided on the quarantine board.",
      guided_cut: "Resolved — a guided cut.",
      accept_deviation: "Resolved — the deviation was accepted; the material stays sellable.",
    },
    optional: "Optional",
    bounds: "Allowed {{min}} to {{max}} {{uom}}",
    boundsAtMost: "At most {{max}} {{uom}}",
    boundsAtLeast: "At least {{min}} {{uom}}",
    boundsTarget: "target {{target}} {{uom}}",
    unbounded: "any",
    measuredLabel: "Measured ({{uom}})",
    measureInvalid: "A number with up to six decimal places.",
    computed: {
      pass: "In range",
      fail: "Out of range",
    },
    // M07-S09's other half, in words: when the instrument said fail there is no
    // Pass control above, and this says why so the absence reads as a rule
    // rather than as a missing button.
    noOverride:
      "The instrument decided this one. A measured failure cannot be recorded as a pass — record it and decide what happens to the material.",
    overrideNote: "Why are you failing a reading that is in range?",
    faultPosition: "Fault position from the A-end (m)",
    record: "Record",
    addPhoto: "Add a photo",
    photosPicked: "Photos attached: {{n}}",
    photosNeeded: "Photos: {{have}} of {{need}}",
    photoRefused: "That photo cannot be used — too large or not an image.",
    progress: "{{done}} of {{total}} checks recorded",
    notes: "Notes — optional",
    complete: "Finish — passed",
    completeFail: "Finish — failed",
    saving: "Saving…",
    blockedMissing: "{{count}} checks still need an answer: {{names}}",
    blockedEvidence: "{{count}} checks still need a photo: {{names}}",
    blockedCause: "Pick what caused the failure.",
    causeLead: "What caused it?",
    causeSearch: "Search causes",
    causeSearchHint: "Type part of a cause…",
    causeEmpty: "No causes are set up yet. A quality lead adds them in the control-point settings.",
    // §4.4's failure-action sheet (07-B-3). Four buttons; the guided cut is
    // ABSENT until 07-C ships it (D-118 — absence, never a disabled shell).
    sheet: {
      lead: "Decide what happens to the material.",
      noAuthority:
        "The failure is recorded. What happens to the material is a quality lead's decision — rework, scrap, quarantine or an accepted deviation.",
      // ONLINE-ONLY (§4.4's dated sentence): the reason rides INSIDE each
      // control, the floor-button pattern (D-118).
      offline: "No network — a decision about material is taken online. It does not queue.",
      // D-299 ruling G5 as narrowed by R8: inside the Rework slab — which
      // stays LIVE — when the inspection's own step belongs to a work order
      // that is no longer released or in progress. It states the fact and the
      // way through (a step on a live order, named in the form); the
      // continuations are named at the blocked Confirm, where they are chosen
      // (spec 07 §4.4). The status label is module 04's own §3.1 vocabulary
      // (`production.status.wo.*`).
      reworkOrderNotLive:
        "This step's work order is {{status}}, so it can't be reopened. To rework, name a step on a live order.",
      // D-299 R8: under a Confirm blocked because the rework would reopen the
      // inspection's own step and its order is finished. Two spellings by
      // principal (owner ruling R3): scrap and accept-deviation are named as
      // the reader's own only when they hold `quality.resolution.dispose`.
      reworkTargetNotLive:
        "The work order is {{status}}. Name a step on a live order above, or scrap the material or accept the deviation instead.",
      reworkTargetNotLiveNoDispose:
        "The work order is {{status}}. Name a step on a live order, or ask a quality lead to scrap the material or accept the deviation.",
      // D-299 R8: a step NAMED in the target field is checked with the rework
      // door's own preview (GET …/rework-target) before Confirm goes live.
      reworkTargetChecking: "Checking that step's work order…",
      reworkTargetUnknown: "No work-order step has that ID. Check it, or leave the field empty.",
      reworkTargetNotCompleted:
        "Step {{seq}} is {{status}}, so there is nothing to reopen. Name the step whose finished work failed.",
      reworkTargetLive: "Rework will reopen step {{seq}}.",
      // D-299 round 4 (D-133, principal-aware): the step the rework would
      // reopen carries module 04's quality hold on the operation, and the
      // preview says THIS reader may not rework a held step. Never "will
      // reopen" — the door would refuse it. {{roles}} is the server's list of
      // the roles that may, each in the reader's words; without a list the
      // seed set is named in words. reworkHeldAsk closes either sentence
      // (holdReleaseSentence joins them), which keeps both under the copy
      // budget (D-425).
      reworkHeldNoRelease:
        "Step {{seq}} has a quality hold on the operation, and only a group administrator, a facility administrator or quality control can rework a held step.",
      reworkHeldNoReleaseRoles:
        "Step {{seq}} has a quality hold on the operation, and only {{roles}} can rework a held step.",
      reworkHeldAsk: "Ask one of them to take this rework, or to clear the hold first.",
      // The preview says this reader may not send the rework through this
      // door for a reason that is NOT the hold — the quarantine door's
      // role-lineage narrowing (D-224). Named with the holders, as D-133 is.
      reworkNotPermittedRoles: "Only {{roles}} can take this rework. Ask one of them.",
      reworkNotPermitted: "This rework is not yours to take. Ask a quality lead.",
      act: {
        rework: "Rework",
        scrap: "Scrap",
        quarantine: "Quarantine",
        accept_deviation: "Accept deviation",
        guided_cut: "Guided cut",
      },
      // The fifth control (07-C). `why.*` is one sentence per server-supplied
      // reason, and the two "…NoMinimum" spellings exist because a control
      // point that names no minimum sellable length imposes none: quoting a
      // minimum that does not exist would be a false statement about the
      // arithmetic the operator is checking on their tape.
      cut: {
        goHint: "Plan the cut on the next screen",
        why: {
          not_offered_by_control_point:
            "This check does not offer a cut. Rework, scrap or accept the deviation instead.",
          subject_not_length_measured:
            "A cut needs a measured length of cable. This check is not about one drum of cable.",
          inspection_not_actionable:
            "This check is not a failure waiting on a decision, so there is nothing to cut.",
          length_not_measured:
            "This drum's length is the machine's claim, not a measurement. Measure it and the cut can be planned.",
          depth_cap:
            "This drum is {{depth}} cuts from the original. Five is the limit — scrap, downgrade or rework it.",
          source_too_short_to_cut:
            "This {{length}} m drum cannot leave a {{minimum}} m sellable piece around a {{trim}} m trim — scrap, downgrade or rework it.",
          source_too_short_to_cut_noMinimum:
            "This {{length}} m drum is not longer than the {{trim}} m trim, so a cut would keep nothing — scrap, downgrade or rework it.",
          no_fault_position:
            "Record where the fault is on the cable first. The cut is planned around that mark.",
          fault_not_on_source:
            "The fault mark is off this {{length}} m drum after a re-measure. Measure again, or correct the mark.",
          no_sellable_segment:
            "A {{trim}} m trim leaves nothing over the {{minimum}} m minimum on this {{length}} m drum, so this is a scrap.",
          no_sellable_segment_noMinimum:
            "A {{trim}} m trim around the fault leaves nothing sellable on this {{length}} m drum. A cut that keeps nothing is a scrap.",
          unknown:
            "This device cannot tell whether a cut is offered here. Reconnect and open the check again.",
        },
        // The in-place fix for the two reasons a re-measure clears.
        fix: {
          open: "Measure this drum",
          label: "Measured length (m)",
          submit: "Save the measurement",
          done: "Measured: {{remaining}} m. The cut can be planned once this check refreshes.",
          noGrant: "Measuring stock is a storekeeper's job — ask for the drum to be re-measured.",
          offline: "No network — a measurement is saved online.",
        },
      },
      confirm: {
        rework: "Send to rework",
        scrap: "Scrap the material",
        quarantine: "Quarantine the stock",
        accept_deviation: "Accept the deviation",
      },
      noteLabel: "Note — optional",
      noteMandatoryLabel: "Why is this deviation acceptable?",
      reasonLabel: "Why is this stock being quarantined?",
      noteRequired: "An accepted deviation carries a mandatory note.",
      reasonRequired: "A quarantine carries a mandatory reason.",
      targetLabel: "Operation to re-open — optional",
      targetHint: "Leave empty to re-open this inspection's own operation",
      targetInvalid: "Paste a full operation ID, or leave it empty.",
      done: {
        rework: "Sent to rework — the producing operation is re-opened.",
        scrap: "Scrapped — recorded in the waste ledger.",
        quarantine: "Quarantined as {{holdNo}}. It is on the quarantine board.",
        accept_deviation: "Deviation accepted — the material stays sellable.",
      },
      reworkRef: "Re-opened operation:",
      scrapValuation: "Scrap valuation: {{amount}}",
      // §7's second bullet, client half: module 05's valuation travels with
      // module 05's OWN visibility set, and the omission is LABELLED here so
      // the boundary between two adjacent money numbers is stated. The copy
      // is deliberately ROLE-NEUTRAL (A2): absence has two causes — a caller
      // outside module 05's set, AND a valuation module 05 never had (§3.5b's
      // zero-vs-uncosted rule) — and the client mirror cannot tell them
      // apart, so the sentence claims neither.
      scrapValuationElsewhere:
        "The scrap valuation, when one was recorded, is in the waste ledger.",
    },
    sync: {
      applied: "Recorded",
      queued: "Waiting to send",
      needsReview: "Needs review",
      refused: "Refused",
      superseded: "Replaced",
    },
    settled: {
      queued: "Saved on this device — it will send itself when the network returns.",
      // NEVER a tick (D-080). The server answered and refused the event into
      // the conflict queue; the office resolves the fact.
      conflicted:
        "This clashes with what is already recorded, so it was not saved. A supervisor must review it.",
      needs_attention: "This was refused. It is in the tray on this device.",
      superseded: "Replaced by a later entry.",
      applied: "Recorded.",
    },
    completed: {
      applied: "Inspection finished.",
      queued: "Finish saved on this device — it will send itself when the network returns.",
      conflicted:
        "This inspection could not be finished. Someone else may have finished it first. Reload and look.",
      needs_attention: "Finishing was refused. It is in the tray on this device.",
      superseded: "Replaced by a later entry.",
    },
    // R2 — the arm's 401: the session is gone, the screen navigates to /login;
    // this is the sentence the outcome note carries for the frame it exists.
    signedOut: "Signed out — sign in again to continue.",
    cancelOpen: "Cancel this inspection…",
    cancelReason: "Why is it being cancelled?",
    cancelConfirm: "Cancel the inspection",
    cancelFailed: "Could not cancel it — it may already be finished.",
  },

  // The floor screens (station context).
  floor: {
    scoreboardTitle: "Shift scoreboard",
    scoreboardDate: "Date",
    scoreboardThing: "the shift scoreboard",
    scoreboardError: "Could not load the scoreboard.",
    scoreboardErrorBody: "Check the connection and try again.",
    scoreboardEmptyTitle: "No shifts on this date",
    scoreboardEmptyBody: "Pick a date your plant ran shifts on.",
    queueTitle: "Inspections",
    runTitle: "Inspection",
    backHome: "Back",
    backQueue: "Back to inspections",
    queueThing: "the inspection list",
    runThing: "this inspection",
    queueError: "Could not load the inspections",
    queueErrorBody: "The list did not come back. Try again when you have signal.",
    runError: "Could not load the inspection",
    runErrorBody: "This inspection did not come back. Try again when you have signal.",
    emptyTitle: "Nothing to inspect",
    emptyBody: "Checks appear here when a job finishes or a receipt lands.",
    refusedTitle: "Not your screen",
    refusedBody: "Your role does not see inspections. Ask your supervisor.",
    queuedCount: "{{count}} entries waiting to send",
    needsReview: "{{count}} entries could not be saved — a supervisor must review them.",
  },

  // S5 — the guided cut screen (floor PWA). Every sentence here is read at a
  // metre through safety glasses, so they are short, and every one of them
  // names the physical thing rather than the record: a drum, a piece, a mark.
  cut: {
    title: "Guided cut",
    back: "Back to the check",
    refusedTitle: "Not your screen",
    refusedBody: "Cutting stock is a quality job. Ask your supervisor.",
    // The one sentence that accounts for every dead control below it.
    onlineOnly:
      "A cut is taken online — it never waits in the queue. Find signal to plan or run it.",
    loadErrorTitle: "Could not load this check",
    loadErrorBody: "It did not come back. Try again when you have signal.",
    sourceLength: "{{length}} m measured",
    depth: "Cut {{depth}} from the original drum",
    notAvailableTitle: "No cut here",
    notPlannedBody: "Plan the cut and the marks will be worked out from the fault position.",
    plan: "Plan the cut",
    planHead: "The plan",
    // §4.5's "name the snapshot the client's own check came from".
    snapshot: "Worked out from the {{length}} m this drum measured when the plan was made.",
    segment: {
      stock: "Keep — {{from}} m to {{to}} m",
      waste: "Cut out — {{from}} m to {{to}} m",
    },
    segmentLength: "{{length}} m",
    belowMinimum: "A piece under the {{minimum}} m minimum goes to scrap, not to stock.",
    tilingBroken:
      "These pieces no longer add up to the whole {{length}} m drum. Plan it again before cutting.",
    tilingBlocked: "The pieces do not add up to the whole drum — plan it again.",
    handlesHead: "Where the cut falls",
    handlesRule: "Marks move outward only — never in past the fault and its trim.",
    handlesFixed: "This check trims nothing, so there is one cut, on the fault. Nothing to move.",
    handleA: "Cut at, from the A-end (m)",
    handleB: "Cut at, from the A-end (m) — the far mark",
    // One sentence per refusal the SERVER's re-tile can give an adjustment, and
    // the key names match its own (`does_not_tile` → unreadable,
    // `window_narrowed` → narrowed, `topology_changed` → topology_changed).
    handleProblem: {
      narrowed: "That would cut inside the fault's trim and sell the fault inside a piece.",
      topology_changed: "That would swallow a whole piece. Plan again with a wider trim instead.",
      unreadable: "Those marks are not two positions on this drum.",
    },
    summaryHead: "Before you cut",
    summaryBody: "{{count}} new drums, each held back until its own retest passes.",
    confirm: "The marks match the drum",
    execute: "Cut the drum",
    executeWarn: "The drum is consumed. This cannot be undone.",
    executeConfirm: "Yes — cut it now",
    executeStandDown: "Not yet",
    replan: "Plan it again",
    cancel: "Abandon this plan",
    cancelReason: "Why is this plan being abandoned?",
    cancelReasonRequired: "Say why, so the next person knows.",
    cancelConfirm: "Abandon the plan",
    cancelledTitle: "This plan was abandoned",
    cancelledBody: "Plan it again when you are ready.",
    lengthChangedHint:
      "The drum was measured again after this plan was made. Plan it again against the new length.",
    doneTitle: "Cut done",
    doneUnits: "{{count}} new drums, held back until their retests pass.",
    doneRetests: "{{count}} retests are waiting on the inspection list.",
    doneToQueue: "Back to inspections",
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type QualityDict = Translated<typeof qualityEn>;

// ru is TRANSLATED (see quality.ru.ts) and pinned by tools/ci/i18n-parity.ts,
// which fails if `ru` ever regresses to en values. The other locales import
// their own quality.<locale>.ts directly, so this module never bundles them.
export const qualityRu: QualityDict = qualityRuDict;
